import type { TourPageThemeKey } from "@/components/sections/tour-page/types";

type PageChromeStyles = {
  wrapper: string;
  pageShell: string;
  skipLink: string;
  mainFocus: string;
};

type HeaderChromeStyles = {
  standard: string;
  overlay: string;
  logoFocus: string;
  navLink: string;
  navActive: string;
  dropdownItem: string;
  dropdownActive: string;
  socialIcon: string;
  phoneCta: string;
  bookingCta: string;
  mobileBookingCta: string;
  mobileMenuButton: string;
  mobileMenuIconColor: string;
  mobileMenuPanel: string;
  mobileMenuOpenAccent: string;
  mobileMenuOpenText: string;
  mobileMenuCloseButton: string;
  mobileMenuLink: string;
  mobileMenuAboutIcon: string;
};

type FooterChromeStyles = {
  shell: string;
  heading: string;
  link: string;
  socialIcon: string;
  iconColor: string;
  bodyText: string;
};

type TourChromeStyles = {
  page: PageChromeStyles;
  header: HeaderChromeStyles;
  footer: FooterChromeStyles;
};

const defaultChromeStyles: TourChromeStyles = {
  page: {
    wrapper: "bg-white",
    pageShell: "",
    skipLink:
      "focus:bg-white focus:text-slate-950 focus:outline-primary-blue",
    mainFocus: "focus-visible:outline-primary-blue",
  },
  header: {
    standard: "sticky bg-white drop-shadow-md",
    overlay:
      "fixed bg-transparent drop-shadow-none xl:sticky xl:bg-white xl:drop-shadow-md",
    logoFocus: "focus-visible:outline-primary-blue",
    navLink: "text-black hover:text-primary-blue focus-visible:outline-primary-blue",
    navActive: "text-primary-blue",
    dropdownItem:
      "border-gray-200 bg-white text-black hover:bg-gray-200 hover:text-primary-blue focus-visible:outline-primary-blue",
    dropdownActive: "text-primary-blue",
    socialIcon: "fill-primary-blue hover:fill-gold",
    phoneCta:
      "border-primary-blue bg-white text-primary-blue hover:bg-gray-100 focus-visible:outline-primary-blue",
    bookingCta:
      "bg-primary-blue text-white hover:bg-primary-blue/80 focus-visible:outline-primary-blue",
    mobileBookingCta:
      "bg-primary-blue text-white hover:bg-primary-blue/80 focus-visible:outline-primary-blue",
    mobileMenuButton: "border-primary-blue hover:bg-gray-100",
    mobileMenuIconColor: "hsl(var(--primary-blue))",
    mobileMenuPanel: "bg-primary-blue",
    mobileMenuOpenAccent: "group-open:bg-blue-300",
    mobileMenuOpenText: "group-open:text-primary-blue",
    mobileMenuCloseButton:
      "border-white/70 text-white hover:bg-white/10 focus-visible:outline-white",
    mobileMenuLink:
      "border-white/25 text-white hover:bg-white/10 focus-visible:outline-white",
    mobileMenuAboutIcon: "text-white",
  },
  footer: {
    shell: "border-gold bg-white",
    heading: "text-dark-blue",
    link: "text-primary-blue hover:text-gold",
    socialIcon: "fill-primary-blue hover:fill-gold",
    iconColor: "#386196",
    bodyText: "text-slate-950",
  },
};

const legacyGhostChromeStyles: TourChromeStyles = {
  page: {
    wrapper: "bg-black",
    pageShell: "bg-black",
    skipLink:
      "focus:bg-white focus:text-slate-950 focus:outline-primary-blue",
    mainFocus: "focus-visible:outline-primary-blue",
  },
  header: {
    standard: "sticky bg-black drop-shadow-md",
    overlay:
      "fixed bg-transparent drop-shadow-none xl:sticky xl:bg-black xl:drop-shadow-md",
    logoFocus: "focus-visible:outline-ghost-red",
    navLink: "text-white hover:text-ghost-red focus-visible:outline-ghost-red",
    navActive: "text-ghost-red",
    dropdownItem:
      "border-gray-200 bg-white text-black hover:bg-gray-200 hover:text-ghost-red focus-visible:outline-ghost-red",
    dropdownActive: "text-ghost-red",
    socialIcon: "fill-white hover:fill-ghost-red",
    phoneCta:
      "border-ghost-red bg-white text-ghost-red hover:bg-gray-100 focus-visible:outline-ghost-red",
    bookingCta:
      "bg-ghost-red text-white hover:bg-ghost-red/80 focus-visible:outline-ghost-red",
    mobileBookingCta:
      "bg-ghost-red text-white hover:bg-ghost-red/80 focus-visible:outline-ghost-red",
    mobileMenuButton: "border-ghost-red hover:bg-gray-100",
    mobileMenuIconColor: "hsl(var(--ghost-red))",
    mobileMenuPanel: "bg-ghost-red",
    mobileMenuOpenAccent: "group-open:bg-gray-200",
    mobileMenuOpenText: "group-open:text-ghost-red",
    mobileMenuCloseButton:
      "border-white/70 text-white hover:bg-white/10 focus-visible:outline-white",
    mobileMenuLink:
      "border-white/25 text-white hover:bg-white/10 focus-visible:outline-white",
    mobileMenuAboutIcon: "text-white",
  },
  footer: {
    shell: "border-ghost-red bg-black",
    heading: "text-white",
    link: "text-white hover:text-ghost-red",
    socialIcon: "fill-ghost-red",
    iconColor: "#bb251d",
    bodyText: "text-white",
  },
};

const themedChromeStyles: TourChromeStyles = {
  page: {
    wrapper: "bg-[var(--tour-page-bg)] text-[var(--tour-page-text)]",
    pageShell: "bg-[var(--tour-page-bg)] text-[var(--tour-page-text)]",
    skipLink:
      "focus:bg-[var(--tour-page-surface)] focus:text-[var(--tour-page-text)] focus:outline-[var(--tour-page-focus)]",
    mainFocus: "focus-visible:outline-[var(--tour-page-focus)]",
  },
  header: {
    standard:
      "sticky border-b border-[var(--tour-chrome-header-border)] bg-[var(--tour-chrome-header-bg)] drop-shadow-md",
    overlay:
      "fixed bg-transparent drop-shadow-none xl:sticky xl:border-b xl:border-[var(--tour-chrome-header-border)] xl:bg-[var(--tour-chrome-header-bg)] xl:drop-shadow-md",
    logoFocus: "focus-visible:outline-[var(--tour-page-focus)]",
    navLink:
      "text-[var(--tour-chrome-nav)] hover:text-[var(--tour-chrome-nav-hover)] focus-visible:outline-[var(--tour-page-focus)]",
    navActive: "text-[var(--tour-chrome-nav-active)]",
    dropdownItem:
      "border-[var(--tour-chrome-dropdown-border)] bg-[var(--tour-chrome-dropdown-bg)] text-[var(--tour-chrome-nav)] hover:bg-[var(--tour-chrome-dropdown-hover-bg)] hover:text-[var(--tour-chrome-nav-hover)] focus-visible:outline-[var(--tour-page-focus)]",
    dropdownActive: "text-[var(--tour-chrome-nav-active)]",
    socialIcon:
      "fill-[var(--tour-chrome-nav-active)] hover:fill-[var(--tour-page-accent-soft)]",
    phoneCta:
      "border-[var(--tour-page-accent)] bg-[var(--tour-chrome-header-bg)] text-[var(--tour-page-accent)] hover:bg-[var(--tour-page-surface-muted)] focus-visible:outline-[var(--tour-page-focus)]",
    bookingCta:
      "bg-[var(--tour-page-button)] text-[var(--tour-page-button-text)] hover:bg-[var(--tour-page-button-hover)] focus-visible:outline-[var(--tour-page-focus)]",
    mobileBookingCta:
      "bg-[var(--tour-page-button)] text-[var(--tour-page-button-text)] hover:bg-[var(--tour-page-button-hover)] focus-visible:outline-[var(--tour-page-focus)]",
    mobileMenuButton:
      "border-[var(--tour-page-accent-soft)] bg-[var(--tour-mobile-menu-button-bg)] hover:bg-[var(--tour-mobile-menu-button-hover)] focus-visible:outline-[var(--tour-page-focus)]",
    mobileMenuIconColor: "var(--tour-mobile-menu-icon)",
    mobileMenuPanel: "bg-[var(--tour-chrome-mobile-panel)]",
    mobileMenuOpenAccent: "group-open:bg-[var(--tour-chrome-mobile-open-accent)]",
    mobileMenuOpenText: "group-open:text-[var(--tour-chrome-mobile-open-text)]",
    mobileMenuCloseButton:
      "border-white/70 text-white hover:bg-white/10 focus-visible:outline-[var(--tour-page-accent-soft)]",
    mobileMenuLink:
      "border-white/20 text-white hover:bg-white/10 focus-visible:outline-[var(--tour-page-accent-soft)]",
    mobileMenuAboutIcon: "text-white",
  },
  footer: {
    shell:
      "border-[var(--tour-chrome-footer-border)] bg-[var(--tour-chrome-footer-bg)]",
    heading: "text-white",
    link:
      "text-[var(--tour-chrome-footer-link)] hover:text-[var(--tour-chrome-footer-link-hover)]",
    socialIcon:
      "fill-[var(--tour-chrome-footer-icon)] hover:fill-white",
    iconColor: "var(--tour-chrome-footer-icon)",
    bodyText: "text-white",
  },
};

export function getTourChromeStyles(
  tourTheme?: TourPageThemeKey,
  isGhostPage = false,
) {
  if (tourTheme) {
    return themedChromeStyles;
  }

  return isGhostPage ? legacyGhostChromeStyles : defaultChromeStyles;
}
